import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import {
  useContext,
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useState,
  useEffect,
} from 'react'
import styles from './styles.module.css'
import { UserContext } from '../../../../base/components/base-container/BaseContainer'
import hasPermission from '../../../../global/utils/user/has-permission'
import { PERMISSIONS } from '../../../../global/constants/permissions'
import { ICasePartial } from '../../../../../../app/entities/Case'
import PrimaryDropdown from '../../../../global/components/dropdowns/PrimaryDropdown'
import PrimaryCheckbox from '../../../../global/components/checkbox/Checkbox'
import {
  CASE_STATUS,
  INVESTIGATION_STATUS,
} from '../../../../global/constants/cases'
import { SSM_PARAMETER_NAMES } from '../../../../global/constants/ssm'
import { fetchData } from '../../../../global/utils/fetch'
import {
  getAwsSsmParameter,
  getTechniciansByLocationObjectId,
} from '../../../api'
import { ITechnician } from '../../../../../../app/entities/Technician'
import CaseHistoryTable from './CaseHistoryTable'
import { IOrder } from '../../../../../../app/entities/Order'

const uniformWidth = { width: '100%' }

interface ICaseModalContentProps {
  technician: ITechnician | null | undefined
  setTechnician: Dispatch<SetStateAction<ITechnician | null | undefined>>
  orderData: IOrder
  caseFormData: ICasePartial
  setCaseFormData: Dispatch<SetStateAction<ICasePartial>>
}
export default function ModalContent({
  technician,
  setTechnician,
  caseFormData,
  setCaseFormData,
  orderData,
}: ICaseModalContentProps) {
  const { user } = useContext(UserContext)

  const [damageCostDisplay, setDamageCostDisplay] = useState<string>('')
  const [riskConnectCostDisplay, setRiskConnectCostDisplay] =
    useState<string>('')
  const [caseTypeItems, setCaseTypeItems] = useState<
    Array<{ key: string; value: string }>
  >([])
  const [caseTypeDescriptions, setCaseTypeDescriptions] = useState<
    Record<string, string>
  >({})
  const [technicians, setTechnicians] = useState<ITechnician[]>([])

  const canCreateCases = hasPermission(user).allow(
    PERMISSIONS.CASES.CATEGORY,
    PERMISSIONS.CASES.ENTRIES.CREATE_CASES.NAME,
  )

  const canEditCases = hasPermission(user).allow(
    PERMISSIONS.CASES.CATEGORY,
    PERMISSIONS.CASES.ENTRIES.EDIT_CASES.NAME,
  )

  const statusItems = Object.values(CASE_STATUS).map((status) => ({
    key: status,
    value: status
      .split('_')
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' '),
  }))

  const investigationStatusItems = Object.entries(INVESTIGATION_STATUS).map(
    ([key, value]) => ({
      key: value,
      value: key
        .split('_')
        .filter(Boolean)
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
        )
        .join(' '),
    }),
  )

  function handleChangeForm(e: ChangeEvent<HTMLInputElement>) {
    setCaseFormData((prevState: ICasePartial) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }))
  }

  function handleCheckboxChange(fieldName: string, checked: boolean) {
    setCaseFormData((prevState: ICasePartial) => ({
      ...prevState,
      [fieldName]: checked,
      // Clear riskConnectCaseNumber and riskConnectCost if isRiskConnect is unchecked
      ...(fieldName === 'isRiskConnect' && !checked
        ? { riskConnectCaseNumber: '', riskConnectCost: undefined }
        : {}),
    }))
    // Reset display value when unchecking RiskConnect
    if (fieldName === 'isRiskConnect' && !checked) {
      setRiskConnectCostDisplay('')
    }
  }

  function handleDamageCostChange(e: ChangeEvent<HTMLInputElement>) {
    const inputValue = e.target.value
    setDamageCostDisplay(inputValue)

    const value = parseFloat(inputValue)
    // Only update if value is positive or empty string (for clearing)
    if (inputValue === '' || (!isNaN(value) && value > 0)) {
      setCaseFormData((prevState: ICasePartial) => ({
        ...prevState,
        damageCost: inputValue === '' ? 0 : value,
      }))
    }
  }

  function handleDamageCostBlur() {
    // Format to 2 decimal places on blur
    if (caseFormData.damageCost && caseFormData.damageCost > 0) {
      const formatted = caseFormData.damageCost.toFixed(2)
      setDamageCostDisplay(formatted)
      setCaseFormData((prevState: ICasePartial) => ({
        ...prevState,
        damageCost: parseFloat(formatted),
      }))
    } else {
      setDamageCostDisplay('0.00')
      setCaseFormData((prevState: ICasePartial) => ({
        ...prevState,
        damageCost: 0,
      }))
    }
  }

  function handleRiskConnectCostChange(e: ChangeEvent<HTMLInputElement>) {
    const inputValue = e.target.value
    setRiskConnectCostDisplay(inputValue)

    const value = parseFloat(inputValue)
    // Only update if value is positive or empty string (for clearing)
    if (inputValue === '' || (!isNaN(value) && value > 0)) {
      setCaseFormData((prevState: ICasePartial) => ({
        ...prevState,
        riskConnectCost: inputValue === '' ? 0 : value,
      }))
    }
  }

  function handleRiskConnectCostBlur() {
    // Format to 2 decimal places on blur
    if (caseFormData.riskConnectCost && caseFormData.riskConnectCost > 0) {
      const formatted = caseFormData.riskConnectCost.toFixed(2)
      setRiskConnectCostDisplay(formatted)
      setCaseFormData((prevState: ICasePartial) => ({
        ...prevState,
        riskConnectCost: parseFloat(formatted),
      }))
    } else {
      setRiskConnectCostDisplay('0.00')
      setCaseFormData((prevState: ICasePartial) => ({
        ...prevState,
        riskConnectCost: 0,
      }))
    }
  }

  // Fetch case types and descriptions from SSM
  useEffect(() => {
    async function fetchAndSetData() {
      try {
        const [caseTypesString, caseTypeDescriptionsString, technicians] =
          await Promise.all([
            fetchData<string>(
              getAwsSsmParameter(SSM_PARAMETER_NAMES.CASE_TYPES),
            ),
            fetchData<string>(
              getAwsSsmParameter(SSM_PARAMETER_NAMES.CASE_TYPE_DESCRIPTIONS),
            ),
            fetchData<ITechnician[]>(
              getTechniciansByLocationObjectId(
                orderData.location?.objectId || '',
              ),
            ),
          ])

        if (caseTypesString) {
          const caseTypesObj = JSON.parse(caseTypesString)
          const items = Object.entries(caseTypesObj).map(([key, value]) => ({
            key,
            value: value as string,
          }))
          setCaseTypeItems(items)
        }

        if (caseTypeDescriptionsString) {
          const descriptionsObj = JSON.parse(caseTypeDescriptionsString)
          setCaseTypeDescriptions(descriptionsObj)
        }

        if (technicians) {
          setTechnicians(technicians)
        }
      } catch (error) {
        console.error('Error fetching data:', error)
      }
    }

    fetchAndSetData()
  }, [])

  // Sync display values with form data
  useEffect(() => {
    if (caseFormData.damageCost) {
      setDamageCostDisplay(caseFormData.damageCost.toString())
    } else {
      setDamageCostDisplay('')
    }
  }, [caseFormData.damageCost])

  useEffect(() => {
    if (caseFormData.riskConnectCost) {
      setRiskConnectCostDisplay(caseFormData.riskConnectCost.toString())
    } else {
      setRiskConnectCostDisplay('')
    }
  }, [caseFormData.riskConnectCost])

  return (
    <div className={styles.inputsContainer}>
      <div className={styles.columnsContainer}>
        {/* Column 1: Work Order Id, Type, Technician Name, Roadie, RiskConnect, Technician Fault */}
        <div className={styles.topInputsContainer}>
          <TextField
            label='Work Order ID'
            name='orderId'
            disabled
            value={caseFormData.order?.workOrderId || ''}
            InputLabelProps={{ shrink: true }}
            sx={uniformWidth}
          />
          <div>
            <PrimaryDropdown
              label='Type'
              defaultValue={caseFormData.type || ''}
              onChange={(newTypeKey: string) => {
                setCaseFormData((prevState: ICasePartial) => ({
                  ...prevState,
                  type: newTypeKey,
                }))
              }}
              items={caseTypeItems}
              disabled={!(canEditCases || canCreateCases)}
            />
            {caseFormData.type && caseTypeDescriptions[caseFormData.type] && (
              <div
                style={{
                  marginTop: 8,
                  padding: '8px 12px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: 4,
                  fontSize: 14,
                  color: '#666',
                  fontStyle: 'italic',
                }}
              >
                {caseTypeDescriptions[caseFormData.type]}
              </div>
            )}
          </div>
          <PrimaryDropdown
            label='Technician'
            defaultValue={technician?.objectId || 'NO_TECHNICIAN'}
            onChange={(newTechnicianObjectId: string) => {
              if (newTechnicianObjectId === 'NO_TECHNICIAN') {
                setTechnician(null)
                setCaseFormData((prevState) => ({
                  ...prevState,
                  isTechnicianFault: false,
                }))
                return
              }
              const selectedTech = technicians.find(
                (tech) => tech.objectId === newTechnicianObjectId,
              )
              setTechnician(selectedTech || null)
            }}
            items={[
              { key: 'NO_TECHNICIAN', value: 'No technician' },
              ...technicians.map((tech) => ({
                key: tech.objectId,
                value: tech.name,
              })),
            ]}
            disabled={!canEditCases} //only an editor can change technician
          />
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <PrimaryCheckbox
              label='Roadie'
              checked={caseFormData.isRoadie || false}
              onChange={(_, checked) =>
                handleCheckboxChange('isRoadie', checked)
              }
              disabled={!(canEditCases || canCreateCases)}
            />
            <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
              <PrimaryCheckbox
                label='RiskConnect'
                checked={caseFormData.isRiskConnect || false}
                onChange={(_, checked) =>
                  handleCheckboxChange('isRiskConnect', checked)
                }
                disabled={!(canEditCases || canCreateCases)}
              />
              {caseFormData.isRiskConnect && (
                <TextField
                  label='Case Number'
                  name='riskConnectCaseNumber'
                  onChange={handleChangeForm}
                  value={caseFormData.riskConnectCaseNumber || ''}
                  sx={{ width: '100%', height: '30px' }}
                  disabled={!(canEditCases || canCreateCases)}
                />
              )}
            </div>
            <PrimaryCheckbox
              label='Technician Fault'
              checked={caseFormData.isTechnicianFault || false}
              onChange={(_, checked) =>
                handleCheckboxChange('isTechnicianFault', checked)
              }
              disabled={!canEditCases || !technician} //only an editor can change technician fault, and a technician must be selected
            />
          </div>
        </div>

        {/* Column 2: Status, DamageCost, Summary */}
        <div className={styles.topInputsContainer}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <div style={{ flex: 1 }}>
              <PrimaryDropdown
                label='Case Status'
                defaultValue={caseFormData.caseStatus || ''}
                onChange={(newStatusKey: string) =>
                  setCaseFormData((prevState: ICasePartial) => ({
                    ...prevState,
                    caseStatus: newStatusKey as CASE_STATUS,
                  }))
                }
                items={statusItems}
                disabled={!(canEditCases || canCreateCases)}
              />
            </div>
            <div style={{ flex: 1 }}>
              <PrimaryDropdown
                label='Investigation Status'
                defaultValue={caseFormData.investigationStatus || ''}
                onChange={(newInvestigationStatus: string) =>
                  setCaseFormData((prevState: ICasePartial) => ({
                    ...prevState,
                    investigationStatus:
                      newInvestigationStatus as INVESTIGATION_STATUS,
                  }))
                }
                items={investigationStatusItems}
                disabled={!canEditCases} // only an editor can change investigation status
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <TextField
              label='Damage Cost'
              name='damageCost'
              type='number'
              onChange={handleDamageCostChange}
              onBlur={handleDamageCostBlur}
              value={damageCostDisplay}
              sx={{ flex: 1 }}
              disabled={!(canEditCases || canCreateCases)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>$</InputAdornment>
                ),
              }}
              inputProps={{
                min: 0.01,
                step: 1.0,
              }}
            />
            {caseFormData.isRiskConnect && (
              <TextField
                label='Risk Connect Cost'
                name='riskConnectCost'
                type='number'
                onChange={handleRiskConnectCostChange}
                onBlur={handleRiskConnectCostBlur}
                value={riskConnectCostDisplay}
                sx={{ flex: 1 }}
                disabled={!(canEditCases || canCreateCases)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>$</InputAdornment>
                  ),
                }}
                inputProps={{
                  min: 0.01,
                  step: 1.0,
                }}
              />
            )}
          </div>
          <TextField
            label='Summary'
            name='summary'
            sx={uniformWidth}
            onChange={handleChangeForm}
            value={caseFormData.summary || ''}
            multiline
            rows={14}
            disabled={!(canEditCases || canCreateCases)}
          />
        </div>

        {/* Column 3: Notes, Risk Score, Case History Table */}
        <div className={styles.topInputsContainer}>
          <TextField
            label='Notes'
            name='note'
            sx={uniformWidth}
            onChange={handleChangeForm}
            value={caseFormData.note || ''}
            multiline
            rows={4}
            disabled={!canEditCases} //only an editor can change notes
          />
          <PrimaryDropdown
            label='Risk Score'
            defaultValue={caseFormData.riskScore?.toString() || ''}
            onChange={(newRiskScore: string) => {
              setCaseFormData((prevState: ICasePartial) => ({
                ...prevState,
                riskScore: newRiskScore
                  ? parseInt(newRiskScore, 10)
                  : undefined,
              }))
            }}
            items={[
              { key: '1', value: '1' },
              { key: '2', value: '2' },
              { key: '3', value: '3' },
              { key: '4', value: '4' },
              { key: '5', value: '5' },
            ]}
            disabled={!canEditCases} //only an editor can change risk score
          />
          <div className={styles.caseHistoryScrollable}>
            <CaseHistoryTable caseHistory={caseFormData.caseHistory || []} />
          </div>
        </div>
      </div>
    </div>
  )
}
